import {
  defaultFields,
  defaultHistoryFields,
} from '@trashlab/api/drizzle/default';
import {
  customer_category,
  isoTimestamp,
  tax_status,
} from '@trashlab/api/src/domains/core/schemas/base-template';
import { PgAddress } from '@trashlab/api/src/domains/core/types';
import { billing_run_merge_strategy } from '@trashlab/api/src/domains/billing/schemas/billing-enums';
import {
  auto_pay_mode,
  dump_ticket_invoice_attachment_mode,
} from '@trashlab/api/src/domains/invoice/schemas/invoice-enums';
import { vendor_payment_method } from '@trashlab/api/src/domains/payables/schemas/payables-enums';
import { payment_policy } from '@trashlab/api/src/domains/customer/schemas/payment-policy';
import { identity_user } from '@trashlab/api/src/domains/identity/schemas/identity-user';
import { customer_segment } from '@trashlab/api/src/domains/pricing/schemas/customer-segment';
import {
  boolean,
  foreignKey,
  jsonb,
  index,
  pgEnum,
  pgTable,
  text,
  uuid,
  varchar,
  numeric,
  integer,
  uniqueIndex,
  check,
} from 'drizzle-orm/pg-core';
import { sql } from 'drizzle-orm';
import { customer_contact } from '@trashlab/api/src/domains/customer/schemas/customer-contact';

export const customer_vendor_kind = pgEnum('customer_vendor_kind', [
  'COMMERCIAL',
  'PUBLIC',
]);

export const customer = pgTable(
  'customer',
  {
    ...defaultFields.fieldsWithTenantId(),
    name: varchar('name', { length: 255 }).notNull(),
    notes: text('notes').default('').notNull(),
    accountId: varchar('accountId', { length: 50 }), // Nullable - accepts any format for legacy imports
    payfacId: varchar('payfacId'),
    lastModifiedBy: uuid('lastModifiedBy'),
    abbreviation: varchar('abbreviation', { length: 8 }),
    createdBy: uuid('createdBy'),
    externalId: varchar('externalId', { length: 255 }),
    formattedName: text('formattedName').notNull(),
    paymentPolicyId: uuid('paymentPolicyId'),
    searchables: text('searchables'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    soldByIdentityUserId: uuid('soldByIdentityUserId'),
    category: customer_category('category').default('RESIDENTIAL'),
    taxStatus: tax_status('taxStatus').default('TAXABLE'),
    sourceId: uuid('sourceId'),
    billingAddress: jsonb('billingAddress').$type<PgAddress>(),
    billingAddressString: text('billingAddressString'),
    billingEmail: varchar('billingEmail'),
    billingName: varchar('billingName'),
    defaultInvoiceMemo: text('defaultInvoiceMemo'),
    billingPhoneNumber: varchar('billingPhoneNumber'),
    doNotSendBilling: boolean('doNotSendBilling').default(false).notNull(),
    sendBillingToEmail: boolean('sendBillingToEmail').default(true).notNull(), //billing preferences for bulk billing
    sendBillingViaText: boolean('sendBillingViaText').default(true).notNull(), //billing preferences for bulk billing
    sendBillingAutoPay: boolean('sendBillingAutoPay').default(false).notNull(), //billing preferences for bulk billing
    sendBillingAutoPayRentalDays: boolean('sendBillingAutoPayRentalDays')
      .default(false)
      .notNull(),
    sendBillingPrint: boolean('sendBillingPrint').default(false).notNull(), //billing preferences for bulk billing [not used, reserved]
    sendBillingViaPhysicalMail: boolean('sendBillingViaPhysicalMail')
      .default(false)
      .notNull(), //billing preferences for bulk billing [not used, reserved]
    // Customer-level override for card surcharge percentage.
    // Null means inherit from billing group; 0 means explicitly no surcharge.
    creditCardSurchargePercentageOverride: numeric(
      'creditCardSurchargePercentageOverride',
      {
        precision: 5,
        scale: 2,
      },
    ),
    // Customer-level preference to hide pricing on scale ticket PDFs.
    // Nullable to allow falling back to location-level settings when unset.
    hidePricingOnTicketPdf: boolean('hidePricingOnTicketPdf'),
    // Customer-level preference to automatically send scale tickets by email.
    // When true, scale tickets will be automatically sent when completed.
    sendScaleTicketsByDefault: boolean('sendScaleTicketsByDefault')
      .default(false)
      .notNull(),
    // Customer-level override for statement inclusion during billing runs.
    // Null means defer to the billing run's effective default (which may come
    // from the billing group/payment policy).
    includeStatementOnInvoiceOverride: boolean(
      'includeStatementOnInvoiceOverride',
    ),
    // Customer-level override for invoice merge strategy during billing runs.
    // When set, overrides the billing run's default merge strategy for this customer.
    // Nullable to allow falling back to billing run's mergeStrategy when unset.
    invoiceMergeStrategy: billing_run_merge_strategy('invoiceMergeStrategy'),
    customerSegmentId: uuid('customerSegmentId').references(
      () => customer_segment.id,
    ),
    // Deprecated: legacy single credit limit (kept for backward-compatibility)
    serviceHoldsIfBalanceExceeds: numeric('serviceHoldsIfBalanceExceeds'),
    // New: Credit limit applied to past-due balance only
    creditLimitPastDue: numeric('creditLimitPastDue'),
    // New: Credit limit applied to open balance
    creditLimitOpen: numeric('creditLimitOpen'),
    // Minimum days overdue before credit hold is applied (aging-based hold)
    creditLimitAgingDays: integer('creditLimitAgingDays'),
    duplicationIndex: integer('duplicationIndex').default(0),
    primaryContactId: uuid('primaryContactId').references(
      () => customer_contact.id,
    ),
    autoPayDays: integer('autoPayDays'),
    /**
     * Autopay scheduling mode for this customer.
     * - DAYS_AFTER_INVOICE: Use autoPayDays to schedule X days after invoice date
     *   (negative values schedule X days before the invoice date)
     * - DAY_OF_MONTH: Use autoPayDayOfMonth to schedule on a specific day each month
     * When null, falls back to organization default (defaultAutoPayMode in organization_settings).
     */
    autoPayMode: auto_pay_mode('autoPayMode'),
    /**
     * Day of month (1-31) when autopay should be scheduled.
     * Only used when autoPayMode is DAY_OF_MONTH.
     * If the month has fewer days, autopay is scheduled on the last day.
     */
    autoPayDayOfMonth: integer('autoPayDayOfMonth'),
    /**
     * Preferred payment method for autopay at customer level.
     * When set, this payment method will be used instead of the default payment method.
     * Site-level defaultPaymentMethodId overrides this when site autopay is enabled.
     */
    defaultPaymentMethodId: uuid('defaultPaymentMethodId'),
    // Nullable per-customer overrides; null inherits the payment policy.
    cardExpirationReminderEnabled: boolean('cardExpirationReminderEnabled'),
    cardExpirationReminderDaysBefore: integer(
      'cardExpirationReminderDaysBefore',
    ),
    cardExpirationReminderFrequencyDays: integer(
      'cardExpirationReminderFrequencyDays',
    ),
    cardExpirationReminderMaxCount: integer('cardExpirationReminderMaxCount'),
    sendPaymentReceipts: boolean('sendPaymentReceipts').default(true).notNull(),
    /**
     * Customer-level override for dump ticket auto-attachment on invoices.
     * When null, the org-level setting is used.
     * When set, overrides the org-level preference for this customer.
     */
    dumpTicketInvoiceAttachmentMode: dump_ticket_invoice_attachment_mode(
      'dumpTicketInvoiceAttachmentMode',
    ),
    // Permit number for customers acting as haulers at scale locations
    permitNumber: varchar('permitNumber', { length: 100 }),

    /**
     * Explicit designation that this account can act as a hauler.
     * Used by recycler logistics and scale workflows to separate role
     * designation from incidental historical relationships.
     */
    isHauler: boolean('isHauler').default(false).notNull(),

    // ==================== VENDOR ROLE FIELDS ====================
    // These fields enable a customer to also act as a vendor (dual-role account)
    // for the AP (Accounts Payable) system. A customer with isVendor=true can
    // receive payable notes and remittances (payments from us to them).

    /**
     * Flag indicating this account can act as a vendor (we can purchase from them).
     * Independent of customer role - an account can be both customer AND vendor.
     * When true, this account can be selected as the vendor for PURCHASE transactions.
     */
    isVendor: boolean('isVendor').default(false).notNull(),

    /**
     * Payment terms for this vendor in days (e.g., 30 = Net 30).
     * Used to calculate due date on payable notes: dueDate = noteDate + vendorPaymentTermsDays
     * Defaults to 0 (due immediately) if not set.
     */
    vendorPaymentTermsDays: integer('vendorPaymentTermsDays').default(0),

    /**
     * Default payment method when creating remittances for this vendor.
     * Can be overridden on individual remittances.
     */
    vendorDefaultPaymentMethod: vendor_payment_method(
      'vendorDefaultPaymentMethod',
    ),

    /**
     * Recycler-specific vendor subtype.
     * - COMMERCIAL: contracted/repeat supplier
     * - PUBLIC: walk-in/public supplier paid at the scale
     */
    vendorKind: customer_vendor_kind('vendorKind'),

    /**
     * Vendor's tax ID for 1099 reporting purposes.
     * Stored but not enforced - W9 requirement is tracking only.
     */
    vendorTaxId: varchar('vendorTaxId', { length: 50 }),

    /**
     * Optional short supplier identifier for recycler-facing lookups/imports.
     */
    supplierCode: varchar('supplierCode', { length: 64 }),

    // Permanent merge alias. This is deliberately separate from archivedAtTs:
    // archive state controls visibility, while this routes concurrent writes.
    mergedIntoCustomerId: uuid('mergedIntoCustomerId'),

    /**
     * Indicates whether we have a W9 on file for this vendor.
     * Tracking only - no enforcement (1099 threshold checking done at year-end).
     */
    vendorW9OnFile: boolean('vendorW9OnFile').default(false),
    njdepCarrierId: varchar('njdepCarrierId', { length: 100 }),
    njdepCabDecal: varchar('njdepCabDecal', { length: 100 }),
    njdepContainerDecal: varchar('njdepContainerDecal', { length: 100 }),
  },
  (table) => {
    return {
      createdAtTs_idx: index('customer_createdAtTs_idx').on(table.createdAtTs),
      tenantId_idx: index('customer_tenantId_idx').on(table.tenantId),
      tenant_createdAtTs_id_active_idx: index(
        'customer_tenant_createdAtTs_id_active_idx',
      )
        .on(table.tenantId, table.createdAtTs.desc(), table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_createdAtTs_id_reconcile_active_idx: index(
        'customer_tenant_createdAtTs_id_reconcile_active_idx',
      )
        .on(table.tenantId, table.createdAtTs, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_updatedAtTs_id_active_idx: index(
        'customer_tenant_updatedAtTs_id_active_idx',
      )
        .on(table.tenantId, table.updatedAtTs.desc(), table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_formatted_name_active_idx: index(
        'customer_tenant_formatted_name_active_idx',
      )
        .on(table.tenantId, table.formattedName, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_name_idx: index('customer_tenant_name_idx').on(
        table.tenantId,
        table.name,
        table.id,
      ),
      quote_global_name_sort_idx: index(
        'customer_quote_global_name_sort_idx',
      ).on(table.tenantId, sql`lower(${table.name}) COLLATE "C"`, table.id),
      tenant_name_active_idx: index('customer_tenant_name_active_idx')
        .on(table.tenantId, table.name, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_legacy_system_id_active_idx: index(
        'customer_tenant_legacy_system_id_active_idx',
      )
        .on(table.tenantId, table.legacySystemId, table.id)
        .where(sql`${table.archivedAtTs} IS NULL`),
      // ECMAScript String.trim parity for SKU import name resolution.
      tenant_js_trim_name_active_idx: index(
        'customer_tenant_js_trim_name_active_idx',
      )
        .on(
          table.tenantId,
          sql`lower(btrim(${table.name}, U&'\\0009\\000A\\000B\\000C\\000D\\0020\\00A0\\1680\\2000\\2001\\2002\\2003\\2004\\2005\\2006\\2007\\2008\\2009\\200A\\2028\\2029\\202F\\205F\\3000\\FEFF'))`,
          table.id,
        )
        .where(sql`${table.archivedAtTs} IS NULL`),
      tenant_normalized_name_active_idx: index(
        'customer_tenant_normalized_name_active_idx',
      )
        .using(
          'btree',
          table.tenantId,
          sql`LOWER(TRIM(${table.name}))`,
          table.id,
        )
        .where(sql`${table.archivedAtTs} IS NULL`),
      lower_name_pattern_finance_charge_idx: index(
        'customer_lower_name_pattern_finance_charge_idx',
      ).using(
        'btree',
        sql`lower(${table.name}) text_pattern_ops`,
        table.tenantId,
        table.id,
      ),
      trigram: index('customer_trigram').using(
        'gin',
        table.tenantId,
        table.searchables.op('gin_trgm_ops'),
      ),
      updatedAtTs_idx: index('customer_updatedAtTs_idx').on(table.updatedAtTs),
      updatedAtTs_idx1: index('customer_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      isHauler_idx: index('customer_isHauler_idx').on(table.isHauler),
      tenant_isHauler_updatedAtTs_idx: index(
        'customer_tenant_isHauler_updatedAtTs_idx',
      ).on(table.tenantId, table.isHauler, table.updatedAtTs.desc()),
      versionNumber_idx: index('customer_versionNumber_idx').on(
        table.versionNumber,
      ),

      paymentPolicyId_idx: index('customer_paymentPolicyId_idx').on(
        table.paymentPolicyId,
      ),
      soldByIdentityUserId_idx: index('customer_soldByIdentityUserId_idx').on(
        table.soldByIdentityUserId,
      ),
      sourceId_idx: index('customer_sourceId_idx').on(table.sourceId),
      customerSegmentId_idx: index('customer_customerSegmentId_idx').on(
        table.customerSegmentId,
      ),
      primaryContactId_idx: index('customer_primaryContactId_idx').on(
        table.primaryContactId,
      ),

      // Index for vendor lookups - enables efficient queries for "list all vendors"
      isVendor_idx: index('customer_isVendor_idx').on(table.isVendor),

      // Composite index for tenant + vendor queries (common for AP operations)
      tenant_isVendor_idx: index('customer_tenant_isVendor_idx').on(
        table.tenantId,
        table.isVendor,
      ),
      vendorKind_idx: index('customer_vendorKind_idx').on(table.vendorKind),
      tenant_isVendor_vendorKind_updatedAtTs_idx: index(
        'customer_tenant_isVendor_vendorKind_updatedAtTs_idx',
      ).on(
        table.tenantId,
        table.isVendor,
        table.vendorKind,
        table.updatedAtTs.desc(),
      ),
      supplierCode_idx: index('customer_supplierCode_idx').on(
        table.supplierCode,
      ),
      mergedIntoCustomerId_idx: index('customer_mergedIntoCustomerId_idx')
        .on(table.tenantId, table.mergedIntoCustomerId, table.id)
        .where(sql`${table.mergedIntoCustomerId} IS NOT NULL`),
      mergedIntoCustomerId_not_self_chk: check(
        'customer_mergedIntoCustomerId_not_self_chk',
        sql`${table.mergedIntoCustomerId} IS NULL OR ${table.mergedIntoCustomerId} <> ${table.id}`,
      ),
      vendorKind_required_when_vendor_chk: check(
        'customer_vendorKind_required_when_vendor_chk',
        sql`(NOT ${table.isVendor} OR ${table.vendorKind} IS NOT NULL)`,
      ),

      // Tenant-scoped unique constraint for account IDs
      // Same accountId can exist in different tenants, but must be unique within a tenant
      accountId_tenantId_unique: uniqueIndex(
        'customer_accountId_tenantId_unique',
      ).on(table.tenantId, table.accountId),

      customer_paymentPolicyId_fkey: foreignKey({
        columns: [table.paymentPolicyId],
        foreignColumns: [payment_policy.id],
        name: 'customer_paymentPolicyId_fkey',
      }),
      customer_soldByIdentityUserId_fkey: foreignKey({
        columns: [table.soldByIdentityUserId],
        foreignColumns: [identity_user.id],
        name: 'customer_soldByIdentityUserId_fkey',
      }),
      customer_sourceId_fkey: foreignKey({
        columns: [table.sourceId],
        foreignColumns: [customer_source.id],
        name: 'customer_sourceId_fkey',
      }),
    };
  },
);

export const customer_history = pgTable('customer_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  name: varchar('name', { length: 255 }),
  notes: text('notes'),
  payfacId: varchar('payfacId'),
  lastModifiedBy: uuid('lastModifiedBy'),
  abbreviation: varchar('abbreviation', { length: 8 }),
  createdBy: uuid('createdBy'),
  externalId: varchar('externalId', { length: 255 }),
  formattedName: text('formattedName'),
  paymentPolicyId: uuid('paymentPolicyId'),
  searchables: text('searchables'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  soldByIdentityUserId: uuid('soldByIdentityUserId'),
  category: customer_category('category'),
  taxStatus: tax_status('taxStatus'),
  sourceId: uuid('sourceId'),
  billingAddress: jsonb('billingAddress').$type<PgAddress>(),
  billingAddressString: text('billingAddressString'),
  billingEmail: varchar('billingEmail'),
  billingName: varchar('billingName'),
  defaultInvoiceMemo: text('defaultInvoiceMemo'),
  billingPhoneNumber: varchar('billingPhoneNumber'),
  doNotSendBilling: boolean('doNotSendBilling').default(false),
  sendBillingToEmail: boolean('sendBillingToEmail'), //billing preferences for bulk billing
  sendBillingViaText: boolean('sendBillingViaText'), //billing preferences for bulk billing
  sendBillingAutoPay: boolean('sendBillingAutoPay'), //billing preferences for bulk billing
  sendBillingAutoPayRentalDays: boolean('sendBillingAutoPayRentalDays'),
  sendBillingPrint: boolean('sendBillingPrint'), //billing preferences for bulk billing [not used, reserved]
  sendBillingViaPhysicalMail: boolean('sendBillingViaPhysicalMail'), //billing preferences for bulk billing [not used, reserved]
  creditCardSurchargePercentageOverride: numeric(
    'creditCardSurchargePercentageOverride',
    {
      precision: 5,
      scale: 2,
    },
  ),
  sendPaymentReceipts: boolean('sendPaymentReceipts').default(true).notNull(),
  hidePricingOnTicketPdf: boolean('hidePricingOnTicketPdf'),
  sendScaleTicketsByDefault: boolean('sendScaleTicketsByDefault'),
  includeStatementOnInvoiceOverride: boolean(
    'includeStatementOnInvoiceOverride',
  ),
  invoiceMergeStrategy: billing_run_merge_strategy('invoiceMergeStrategy'),
  customerSegmentId: uuid('customerSegmentId'),
  serviceHoldsIfBalanceExceeds: numeric('serviceHoldsIfBalanceExceeds'),
  creditLimitPastDue: numeric('creditLimitPastDue'),
  creditLimitOpen: numeric('creditLimitOpen'),
  creditLimitAgingDays: integer('creditLimitAgingDays'),
  duplicationIndex: integer('duplicationIndex'),
  primaryContactId: uuid('primaryContactId'),
  autoPayDays: integer('autoPayDays'),
  autoPayMode: auto_pay_mode('autoPayMode'),
  autoPayDayOfMonth: integer('autoPayDayOfMonth'),
  defaultPaymentMethodId: uuid('defaultPaymentMethodId'),
  cardExpirationReminderEnabled: boolean('cardExpirationReminderEnabled'),
  cardExpirationReminderDaysBefore: integer('cardExpirationReminderDaysBefore'),
  cardExpirationReminderFrequencyDays: integer(
    'cardExpirationReminderFrequencyDays',
  ),
  cardExpirationReminderMaxCount: integer('cardExpirationReminderMaxCount'),
  accountId: varchar('accountId', { length: 50 }),
  permitNumber: varchar('permitNumber', { length: 100 }),
  isHauler: boolean('isHauler'),
  // Vendor fields
  isVendor: boolean('isVendor'),
  vendorPaymentTermsDays: integer('vendorPaymentTermsDays'),
  vendorDefaultPaymentMethod: vendor_payment_method(
    'vendorDefaultPaymentMethod',
  ),
  vendorKind: customer_vendor_kind('vendorKind'),
  vendorTaxId: varchar('vendorTaxId', { length: 50 }),
  supplierCode: varchar('supplierCode', { length: 64 }),
  vendorW9OnFile: boolean('vendorW9OnFile'),
  mergedIntoCustomerId: uuid('mergedIntoCustomerId'),
  njdepCarrierId: varchar('njdepCarrierId', { length: 100 }),
  njdepCabDecal: varchar('njdepCabDecal', { length: 100 }),
  njdepContainerDecal: varchar('njdepContainerDecal', { length: 100 }),
});

export const customer_source = pgTable(
  'customer_source',
  {
    ...defaultFields.fieldsWithTenantId(),
    lastModifiedBy: uuid('lastModifiedBy'),
    createdBy: uuid('createdBy'),
    legacySystemId: varchar('legacySystemId', { length: 255 }),
    name: varchar('name', { length: 255 }).notNull(),
  },
  (table) => {
    return {
      createdAtTs_idx: index('customer_source_createdAtTs_idx').on(
        table.createdAtTs,
      ),
      tenantId_idx: index('customer_source_tenantId_idx').on(table.tenantId),
      name_tenantId_key: uniqueIndex('customer_source_name_tenantId_key').on(
        table.name,
        table.tenantId,
      ),
      updatedAtTs_idx: index('customer_source_updatedAtTs_idx').on(
        table.updatedAtTs,
      ),
      updatedAtTs_idx1: index('customer_source_updatedAtTs_idx1').on(
        table.updatedAtTs.desc(),
      ),
      versionNumber_idx: index('customer_source_versionNumber_idx').on(
        table.versionNumber,
      ),
    };
  },
);

export const customer_source_history = pgTable('customer_source_history', {
  ...defaultHistoryFields.historyFieldsWithTenantId(),
  lastModifiedBy: uuid('lastModifiedBy'),
  createdBy: uuid('createdBy'),
  legacySystemId: varchar('legacySystemId', { length: 255 }),
  name: varchar('name', { length: 255 }),
});

export const customer_contact_pivot = pgTable(
  'customer_contact_pivot',
  {
    id: uuid('id').defaultRandom().primaryKey(),
    customerId: uuid('customerId').notNull(),
    contactId: uuid('contactId').notNull(),
    tenantId: uuid('tenantId').notNull(),
    isDefaultBillingCC: boolean('isDefaultBillingCC').default(false).notNull(),
    customerSiteId: uuid('customerSiteId'),
    createdAtTs: isoTimestamp('createdAtTs', {
      withTimezone: true,
    })
      .default(sql`now()`)
      .notNull(),
    updatedAtTs: isoTimestamp('updatedAtTs', {
      withTimezone: true,
    })
      .default(sql`now()`)
      .notNull(),
  },
  (table) => {
    return {
      site_merge_page_idx: index('contact_pivot_site_merge_page_idx').on(
        table.tenantId,
        table.customerSiteId,
        table.id,
      ),
      contactid_dx: index('customer_contact_pivot_contactid_dx').on(
        table.contactId,
      ),
      contactid_tenantid_dx: index(
        'customer_contact_pivot_contactid_tenantid_dx',
      ).on(table.contactId, table.tenantId),
      customerid_dx: index('customer_contact_pivot_customerid_dx').on(
        table.customerId,
        table.tenantId,
      ),
      tenant_customer_id_idx: index(
        'customer_contact_pivot_tenant_customer_id_idx',
      ).on(table.tenantId, table.customerId, table.id),
      tenantid_dx: index('customer_contact_pivot_tenantid_dx').on(
        table.tenantId,
      ),
      tenant_site_contact_idx: index(
        'customer_contact_pivot_tenant_site_contact_idx',
      )
        .on(table.tenantId, table.customerSiteId, table.contactId)
        .where(sql`${table.customerSiteId} IS NOT NULL`),

      customer_contact_pivot_contactId_fkey: foreignKey({
        columns: [table.contactId],
        foreignColumns: [customer_contact.id],
        name: 'customer_contact_pivot_contactId_fkey',
      }),
      customer_contact_pivot_customerId_fkey: foreignKey({
        columns: [table.customerId],
        foreignColumns: [customer.id],
        name: 'customer_contact_pivot_customerId_fkey',
      }),
    };
  },
);

export type CustomerSchema = typeof customer.$inferSelect;
